import { Link } from "react-router-dom";
import {
    Table,
    TableHeader,
    TableColumn,
    TableBody,
    TableRow,
    TableCell,
    Card,
    CardHeader,
    CardBody,
    CardFooter,
    Button,
    Tooltip,
} from "@heroui/react";
import { LuPencil, LuPlus } from "react-icons/lu";
import { useDataTable, useFetchData } from "@/hooks";
import {
    DataTableControlBar,
    DataTableSkeleton,
    DataTablePagination,
    FilterButton,
    ActiveFilterChips,
} from "@/components/data-table";
import {
    EmptyState,
    ErrorState,
    PageHeader,
    ThemedText,
} from "@/components/ui";
import { FilterData } from "./components";
import { findOptionTitle, formatCurrency, formatDate } from "@/utils";
import { useUser } from "@/context/UserContext";

const CashTransferData = () => {
    const { hasPermission } = useUser();
    const hasAnyAction = hasPermission("cash-transfer.edit");
    const initialFilters = {
        search: "",
        start_date: "",
        end_date: "",
        source_master_cash_id: "",
        destination_master_cash_id: "",
    };

    const table = useDataTable(
        "cash-transfer/data",
        null, // No delete url
        {},
        initialFilters,
    );

    const { data: masterCashes } = useFetchData("select/master-cash");

    const filterLabels = {
        search: { label: "Pencarian" },
        start_date: {
            label: "Tanggal Mulai",
            getValueLabel: (val) => formatDate(val),
        },
        end_date: {
            label: "Tanggal Akhir",
            getValueLabel: (val) => formatDate(val),
        },
        source_master_cash_id: {
            label: "Kas Asal",
            getValueLabel: (val) => findOptionTitle(masterCashes, val),
        },
        destination_master_cash_id: {
            label: "Kas Tujuan",
            getValueLabel: (val) => findOptionTitle(masterCashes, val),
        },
    };

    const handleRemoveFilter = (key) => {
        table.filterPanel.setPending((prev) => ({
            ...prev,
            [key]: initialFilters[key],
        }));
        table.filters.setFilter(key, initialFilters[key]);
    };

    const skeletonColumns = [
        { name: "No.", skeletonWidth: "w-8" },
        { name: "Tanggal", skeletonWidth: "w-24" },
        { name: "Kas Asal", skeletonWidth: "w-32" },
        { name: "Kas Tujuan", skeletonWidth: "w-32" },
        { name: "Keterangan", skeletonWidth: "w-64" },
        { name: "Nominal", skeletonWidth: "w-32", align: "end" },
        ...(hasAnyAction ? [{ name: "Aksi", skeletonWidth: "w-20", align: "end" }] : []),
    ];

    if (table.error) {
        return (
            <div className="p-6">
                <ErrorState
                    title="Gagal Memuat Data"
                    description="Terjadi kesalahan saat memuat data Transfer Kas."
                    onAction={table.refetch}
                />
            </div>
        );
    }

    return (
        <div className="flex h-full w-full flex-col gap-4">
            <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
                <PageHeader
                    title="Transfer Kas"
                    breadcrumbs={[
                        { label: "Dashboard", href: "/" },
                        { label: "Keuangan", href: "/finance" },
                        { label: "Transfer Kas" },
                    ]}
                />
                <div className="flex items-center gap-2">
                    <FilterButton
                        isOpen={table.filterPanel.isOpen}
                        onToggle={() =>
                            table.filterPanel.setIsOpen(
                                !table.filterPanel.isOpen,
                            )
                        }
                        activeCount={table.filters.activeCount}
                    />

                    {hasPermission("cash-transfer.create") && (
                        <Button
                            as={Link}
                            to="/cash-transfer/create"
                            color="primary"
                            startContent={<LuPlus />}
                            size="sm"
                        >
                            Buat Transfer
                        </Button>
                    )}
                </div>
            </div>

            <FilterData
                table={table}
                initialFilters={initialFilters}
                filterOptions={{ masterCashes: masterCashes || [] }}
            />

            <ActiveFilterChips
                filters={table.filters.values}
                filterLabels={filterLabels}
                onRemove={handleRemoveFilter}
            />

            <Card className="border-default-200 border" shadow="none">
                <CardHeader>
                    <DataTableControlBar table={table} />
                </CardHeader>
                <CardBody className="p-0">
                    {table.isLoading ? (
                        <DataTableSkeleton
                            columns={skeletonColumns}
                            rowCount={table.pagination.perPage}
                        />
                    ) : table.items.length === 0 ? (
                        <EmptyState
                            title={
                                table.filters.activeCount > 0
                                    ? "Data Tidak Ditemukan"
                                    : "Belum Ada Data"
                            }
                            description={
                                table.filters.activeCount > 0
                                    ? "Tidak ada data yang cocok dengan filter."
                                    : "Belum ada riwayat Transfer Kas."
                            }
                        />
                    ) : (
                        <Table aria-label="Cash Transfer Table" shadow="none">
                            <TableHeader>
                                <TableColumn>No.</TableColumn>
                                <TableColumn>Tanggal</TableColumn>
                                <TableColumn>Kas Asal</TableColumn>
                                <TableColumn>Kas Tujuan</TableColumn>
                                <TableColumn>Keterangan</TableColumn>
                                <TableColumn align="end">Nominal</TableColumn>
                                {hasAnyAction && <TableColumn align="end">Aksi</TableColumn>}
                            </TableHeader>
                            <TableBody items={table.items}>
                                {(item) => (
                                    <TableRow key={item.id}>
                                        <TableCell>
                                            <ThemedText
                                                size="sm"
                                                variant="secondary"
                                            >
                                                {(table.pagination.page - 1) *
                                                    table.pagination.perPage +
                                                    table.items.indexOf(item) +
                                                    1}
                                                .
                                            </ThemedText>
                                        </TableCell>
                                        <TableCell>
                                            <ThemedText size="sm">
                                                {formatDate(
                                                    item.transaction_date,
                                                    "dd/MM/yyyy HH:mm",
                                                )}
                                            </ThemedText>
                                        </TableCell>
                                        <TableCell>
                                            <ThemedText
                                                size="sm"
                                                className="font-medium"
                                            >
                                                {item.master_cash?.name || "-"}
                                            </ThemedText>
                                        </TableCell>
                                        <TableCell>
                                            <ThemedText
                                                size="sm"
                                                className="font-medium"
                                            >
                                                {item.transfer_destination
                                                    ?.master_cash?.name || "-"}
                                            </ThemedText>
                                        </TableCell>
                                        <TableCell>
                                            <ThemedText
                                                size="sm"
                                                className="line-clamp-2"
                                            >
                                                {item.description || "-"}
                                            </ThemedText>
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex justify-end">
                                                <ThemedText size="sm">
                                                    {formatCurrency(
                                                        item.amount,
                                                    )}
                                                </ThemedText>
                                            </div>
                                        </TableCell>
                                        {hasAnyAction && (
                                            <TableCell>
                                                <div className="flex justify-end gap-2">
                                                    <Tooltip content="Edit">
                                                        <Button
                                                            isIconOnly
                                                            size="sm"
                                                            variant="light"
                                                            as={Link}
                                                            to={`/cash-transfer/${item.id}/edit`}
                                                        >
                                                            <LuPencil
                                                                size={14}
                                                                className="text-default-500"
                                                            />
                                                        </Button>
                                                    </Tooltip>
                                                </div>
                                            </TableCell>
                                        )}
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    )}
                </CardBody>
                <CardFooter>
                    <DataTablePagination table={table} />
                </CardFooter>
            </Card>
        </div>
    );
};

export default CashTransferData;
